import type { GroupBase, GroupProps, OnChangeValue } from 'react-select';

import isEqual from 'fast-deep-equal';

/**
 * Renders a custom group component for use with react-select, allowing for optional clickable group headers.
 *
 * @template Option - The type of the option objects.
 * @template IsMulti - Boolean indicating if multiple selections are allowed.
 * @template Group - The group type extending GroupBase.
 *
 * @param props - The props for the Group component.
 * @param props.children - The child nodes to render within the group.
 * @param props.cx - Function for generating class names.
 * @param props.data - The group data, including options.
 * @param props.getClassNames - Function to get class names for styling.
 * @param props.getStyles - Function to get styles for the group.
 * @param props.getValue - Function to get the currently selected values.
 * @param props.Heading - The heading component for the group.
 * @param props.headingProps - Props to pass to the heading component.
 * @param props.label - The label for the group.
 * @param props.selectProps - Additional props passed to the select component, including `allowClickableHeader`.
 * @param props.setValue - Function to update the selected values.
 * @param props.theme - The theme object for styling.
 *
 * @returns The rendered group component.
 *
 * @remarks
 * If `allowClickableHeader` is true in `selectProps`, clicking the group header will select all options in the group
 * that are not already selected.
 */
function Group<Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  children,
  cx,
  data,
  getClassNames,
  getStyles,
  getValue,
  Heading,
  headingProps,
  label,
  selectProps,
  setValue,
  theme,
}: GroupProps<Option, IsMulti, Group>) {
  const { allowClickableHeader = false } = selectProps;

  return (
    <div
      className="hsp-select-group"
      onClick={(e) => {
        if (allowClickableHeader) {
          const currentValues = getValue();
          const { options } = data;
          const addedValues = options.filter((item) => {
            return currentValues.every((value) => {
              return !isEqual(value, item);
            });
          });
          const updatedValues = [...new Set([...addedValues, ...currentValues])];

          e.stopPropagation();
          setValue(updatedValues as OnChangeValue<Option, IsMulti>, 'select-option');
        }
      }}>
      <Heading
        {...headingProps}
        cx={cx}
        getClassNames={getClassNames}
        getStyles={getStyles}
        selectProps={selectProps}
        theme={theme}>
        {label}
      </Heading>
      <div>{children}</div>
    </div>
  );
}

export default Group;
